/**
 * 文件签名功能常量定义
 */

// 区域类型
export const REGION_TYPE = {
  SIGNATURE: "signature", // 签字区
  TEXT: "text", // 文本区
  INPUT: "input", // 输入区
};

// 区域类型配置
// 注意：defaultWidthPx 和 defaultHeightPx 是固定像素值，创建时会根据图片实际尺寸转换为百分比
export const REGION_TYPE_CONFIG = {
  [REGION_TYPE.SIGNATURE]: {
    label: "签字区",
    icon: "signature",
    borderColor: "#FF4D4D",
    backgroundColor: "rgba(255, 77, 77, 0.1)",
    borderStyle: "dashed",
    defaultWidthPx: 160, // 默认宽度（像素）
    defaultHeightPx: 60, // 默认高度（像素）
    minWidth: 160, // 最小宽度 px
    minHeight: 60, // 最小高度 px
  },
  [REGION_TYPE.TEXT]: {
    label: "文本区",
    icon: "text",
    borderColor: "#4A90E2",
    backgroundColor: "rgba(74, 144, 226, 0.1)",
    borderStyle: "solid",
    defaultWidthPx: 160, // 默认宽度（像素）
    defaultHeightPx: 60, // 默认高度（像素）
    minWidth: 160, // 最小宽度 px
    minHeight: 60, // 最小高度 px
  },
  [REGION_TYPE.INPUT]: {
    label: "输入区",
    icon: "input",
    borderColor: "#52C41A",
    backgroundColor: "rgba(82, 196, 26, 0.1)",
    borderStyle: "dashed",
    defaultWidthPx: 160, // 默认宽度（像素）- 足够容纳"请点击输入"
    defaultHeightPx: 60, // 默认高度（像素）
    minWidth: 160, // 最小宽度 px - 足够容纳一行文字
    minHeight: 60, // 最小高度 px
  },
};

// 预设颜色
export const PRESET_COLORS = [
  { value: "#333333", label: "黑色" },
  { value: "#666666", label: "灰色" },
  { value: "#FFB800", label: "橙色" },
  { value: "#4A90E2", label: "蓝色" },
  { value: "#FF4D4D", label: "红色" },
  { value: "#52C41A", label: "绿色" },
];

// 字体大小选项（使用 rpx 单位）
export const FONT_SIZE_OPTIONS = [
  { value: 24, label: "字号：24" },
  { value: 28, label: "字号：28" },
  { value: 32, label: "字号：32" },
  { value: 36, label: "字号：36" },
  { value: 40, label: "字号：40" },
  { value: 44, label: "字号：44" },
];

// 默认字体大小（rpx）
export const DEFAULT_FONT_SIZE = 24;

// 默认字体颜色
export const DEFAULT_FONT_COLOR = "#333333";

// 编辑模式
export const EDIT_MODE = {
  EDIT: "edit", // 编辑模式
  PREVIEW: "preview", // 预览模式
};

// 工具类型
export const TOOL_TYPE = {
  NONE: "none", // 无选中工具
  SIGNATURE: "signature", // 添加签字区
  TEXT: "text", // 添加文本区
  INPUT: "input", // 添加输入区
};

// 生成唯一ID
export const generateId = (prefix = "region") => {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// 创建空白区域
// containerSize: { width, height } - 图片容器的实际像素尺寸
export const createRegion = (type, position, pageId, containerSize) => {
  const config = REGION_TYPE_CONFIG[type];

  // 计算默认宽高的百分比
  let defaultWidth = 0.25; // 默认25%
  let defaultHeight = 0.05; // 默认5%（降低以避免 iOS 上过高）

  if (containerSize && containerSize.width > 0 && containerSize.height > 0) {
    // 根据固定像素值和容器尺寸计算百分比
    defaultWidth = Math.min(config.defaultWidthPx / containerSize.width, 0.8);
    defaultHeight = Math.min(
      config.defaultHeightPx / containerSize.height,
      0.5
    );
  }

  // 支持两种定位方式：左上角(x, y) 或 中心点(centerX, centerY)
  let x, y;
  if (position.centerX !== undefined && position.centerY !== undefined) {
    // 使用中心点定位，计算左上角坐标
    x = position.centerX - defaultWidth / 2;
    y = position.centerY - defaultHeight / 2;
  } else {
    // 使用左上角定位
    // x=-1 表示水平居中
    if (position.x === -1) {
      x = (1 - defaultWidth) / 2; // 水平居中
    } else {
      x = position.x;
    }
    y = position.y;
  }

  // 确保不超出边界
  x = Math.max(0, Math.min(x, 1 - defaultWidth));
  y = Math.max(0, Math.min(y, 1 - defaultHeight));

  const baseRegion = {
    id: generateId(),
    pageId,
    type,
    position: {
      x: x,
      y: y,
      width: defaultWidth,
      height: defaultHeight,
    },
    style: {
      borderColor: config.borderColor,
      backgroundColor: config.backgroundColor,
    },
    config: {},
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };

  // 根据类型设置默认配置
  switch (type) {
    case REGION_TYPE.SIGNATURE:
      baseRegion.config = {
        placeholder: "签字区",
        required: true,
      };
      break;
    case REGION_TYPE.TEXT:
      baseRegion.config = {
        content: "",
        fontSize: DEFAULT_FONT_SIZE,
        fontColor: DEFAULT_FONT_COLOR,
      };
      break;
    case REGION_TYPE.INPUT:
      baseRegion.config = {
        placeholder: "请点击输入",
        fontSize: DEFAULT_FONT_SIZE,
        fontColor: DEFAULT_FONT_COLOR,
        required: true,
      };
      break;
  }

  return baseRegion;
};

// 验证区域位置是否合法（在图片范围内）
export const validatePosition = (position) => {
  return (
    position.x >= 0 &&
    position.y >= 0 &&
    position.x + position.width <= 1 &&
    position.y + position.height <= 1
  );
};

// 限制区域位置在图片范围内
export const clampPosition = (position) => {
  const clamped = { ...position };

  // 限制 x
  if (clamped.x < 0) clamped.x = 0;
  if (clamped.x + clamped.width > 1) clamped.x = 1 - clamped.width;

  // 限制 y
  if (clamped.y < 0) clamped.y = 0;
  if (clamped.y + clamped.height > 1) clamped.y = 1 - clamped.height;

  return clamped;
};
